import type { Ref } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import { Routes } from '@/utils/navigation/routes'
import { rememberOnboardingDoor } from '@/utils/onboardingEntry'
import { Typography } from '@/UI-KIT/Typography/Typography'

import { HeroPick } from './hero/HeroPick'
import styles from './LandingHero.module.scss'

/**
 * Headline, one line of what the product does, the ask — and the figure that argues for all three.
 *
 * The proof used to be here too, twice: a whisper-scale trust bar above the headline and the awards
 * under the CTAs. Both now live in one TrustBand below the hero, at the parent site's own logo
 * scale. Proof still lands after the ask, which is the point; it just does not compete with the
 * headline for the top of the screen any more.
 */
type LandingHeroProps = {
  /** The row of buttons, for the phone's pinned ask (LandingStickyCta) to know when it has gone. */
  ctaRef?: Ref<HTMLDivElement>
}

export const LandingHero = ({ ctaRef }: LandingHeroProps) => {
  const { t } = useTranslation()

  return (
    <div className={styles.root}>
      <div className={styles.copy}>
        <Typography variant="h1" color="heading">
          {/* The accented half of the headline, the way intch.org splits its own: the meaningful
              noun phrase in periwinkle, the connective words left in ink. */}
          <Trans
            i18nKey="landing.hero.headline"
            components={{ hl: <span className={styles.highlight} /> }}
          />
        </Typography>

        <Typography variant="t1" color="body" className={styles.sub}>
          {t('landing.hero.subheadline')}
        </Typography>

        <div ref={ctaRef} className={styles.ctas}>
          <Link
        className={`${styles.button} ${styles.buttonPrimary}`}
        to={Routes.onboard}
        onClick={() => rememberOnboardingDoor('hero')}
      >
            {t('landing.cta.primary')}&nbsp;&nbsp;→
          </Link>
          <a className={`${styles.button} ${styles.buttonSecondary}`} href="#features">
            {t('landing.cta.secondary')}
          </a>
        </div>
      </div>

      {/* stretch, not centre: the panel takes its height from the row, so the two hero columns can
          never drift apart when the type scales. */}
      <div className={styles.stage}>
        <HeroPick />
      </div>
    </div>
  )
}
